import { useState, type ReactNode } from 'react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useRouter } from '@tanstack/react-router';
import { JourneysHandoffView } from '@/services/router/journeys-handoff-view';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { SafeNavigate } from '@/services/router/safe-navigate';
import {
  DEFAULT_DATA_INDEX_TYPE,
  dataIndexItemPath,
  dataIndexListPath,
  legacyDataIndexItemPath,
  legacyDataIndexListPath,
  legacyListTypeFor,
  type DataIndexItemAddress,
  type DataIndexListType,
} from './urls';

type DataIndexRouteProps = {
  type: DataIndexListType;
  children: ReactNode;
};

/**
 * Which app serves the data index while `data-index-migration` rolls out.
 *
 * Flag off, the embedded app serves it and the migrated URL forwards to the
 * URL it answers; flag on, this app serves it and the `journeys` URL forwards
 * here, so links written either way keep working without being re-pointed.
 *
 * Conversions is the one list with no URL to forward to, so it forwards to
 * the list the embedded app lands on instead — see `legacyListTypeFor`.
 */
export function DataIndexRoute({ type, children }: DataIndexRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataIndexMigration);
  const legacyHref = useMigrationHandoffHref((workspaceId) =>
    legacyDataIndexListPath(workspaceId, legacyListTypeFor(type)),
  );
  // Readiness gates on the resolved workspace, not the URL segment: the reads
  // below go through `requireWorkspaceId`, which throws while the workspaces
  // list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      {children}
    </MigrationRouteView>
  );
}

type DataIndexItemRouteProps = {
  address: DataIndexItemAddress;
  children: ReactNode;
};

/** The same handoff for one item's own page, at its own two URL shapes. */
export function DataIndexItemRoute({ address, children }: DataIndexItemRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataIndexMigration);
  const legacyHref = useMigrationHandoffHref((workspaceId) =>
    legacyDataIndexItemPath(workspaceId, address.kind, address.name, address.scope),
  );
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      {children}
    </MigrationRouteView>
  );
}

type JourneysDataIndexItemHandoffProps = {
  address: DataIndexItemAddress;
};

/** The `journeys` spelling of an item page, which forwards once the flag is on. */
export function JourneysDataIndexItemHandoff({ address }: JourneysDataIndexItemHandoffProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataIndexMigration);
  const migratedHref = useMigrationHandoffHref((workspaceId) =>
    dataIndexItemPath(workspaceId, address.kind, address.name, address.scope),
  );

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

type JourneysDataIndexHandoffProps = {
  type: DataIndexListType;
};

/** The `journeys` spelling of a type list, which forwards once the flag is on. */
export function JourneysDataIndexHandoff({ type }: JourneysDataIndexHandoffProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataIndexMigration);
  const migratedHref = useMigrationHandoffHref((workspaceId) => dataIndexListPath(workspaceId, type));

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={migratedHref}>
      <Journeys />
    </JourneysHandoffView>
  );
}

/**
 * The migrated landing URL, which has no list of its own: it opens the attributes
 * browser in whichever app is serving it, replacing itself in history so Back
 * does not land on it again.
 *
 * Arriving from a list means the nav item was clicked while already in the
 * data index, so the reader goes back to the browser they were reading
 * rather than to the default one. This URL renders nothing itself, so it
 * always has to resolve to a list.
 */
export function DataIndexIndexRedirect() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataIndexMigration);
  const cameFromAList = useCameFromDataIndexList();
  const defaultList = useMigrationHandoffHref((workspaceId) =>
    isMigrated
      ? dataIndexListPath(workspaceId, DEFAULT_DATA_INDEX_TYPE)
      : legacyDataIndexListPath(workspaceId, DEFAULT_DATA_INDEX_TYPE),
  );

  const target = dataIndexLandingTarget(cameFromAList, defaultList);
  if (target == null) {
    return null;
  }
  return <SafeNavigate to={target} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * The `journeys` spelling of the landing URL.
 *
 * Flag off it renders the embedded host, which redirects to its own
 * attributes list. Redirecting from here instead would park the transition:
 * the target is an embedded URL, so the navigation waits for a host that a
 * redirect-only route never mounts, and the pane stays blank for everyone
 * the flag is off for.
 */
export function JourneysDataIndexIndexHandoff() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.DataIndexMigration);
  const cameFromAList = useCameFromDataIndexList();
  const defaultList = useMigrationHandoffHref((workspaceId) => dataIndexListPath(workspaceId, DEFAULT_DATA_INDEX_TYPE));

  return (
    <JourneysHandoffView isMigrated={isMigrated} migratedHref={dataIndexLandingTarget(cameFromAList, defaultList)}>
      <Journeys />
    </JourneysHandoffView>
  );
}

const DATA_INDEX_LIST_PATHNAME = /\/data-index\/[^/]+\/?$/i;

/**
 * Whether the URL before this one was already a data-index list, which means
 * the nav item was clicked from inside the data index.
 */
export function isDataIndexListPathname(pathname: string | undefined): boolean {
  return pathname != null && DATA_INDEX_LIST_PATHNAME.test(pathname);
}

/** Where the URL before this one was, if the router still knows. */
function useCameFromDataIndexList(): PreviousLocation {
  const router = useRouter();
  // Captured on the first render, before the router promotes the new location:
  // after that it is no longer possible to tell where the reader came from.
  const [cameFrom] = useState(() => router.state.resolvedLocation);
  return cameFrom == null ? undefined : { pathname: cameFrom.pathname, href: cameFrom.href };
}

export type PreviousLocation = { pathname: string; href: string } | undefined;

/**
 * Where the landing URL sends the reader.
 *
 * Back to the list they were reading, narrowing and all, when the nav item
 * was clicked from inside the data index; otherwise to the default browser.
 * Never nowhere: this URL renders nothing of its own, so returning null here
 * leaves a blank page.
 */
export function dataIndexLandingTarget(previous: PreviousLocation, defaultList: string | null): string | null {
  return isDataIndexListPathname(previous?.pathname) ? (previous?.href ?? null) : defaultList;
}
